/* Copyright CloudLinux Zug GmbH 2010-2017 All Rights Reserved
 *
 * Licensed under CLOUD LINUX LICENSE AGREEMENT
 * http://cloudlinux.com/docs/LICENSE.TXT
 */

import { Component, AfterViewInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Router }  from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { SelectorRepo } from '../../repository/selector_repo';
import { BaseComponent } from '../../utils/base_component';
import { NodeJSInformation } from '../model';
import { Column } from '../../utils/column';
import { Caption } from '../../model/caption';
import { Icon } from '../../model/icon';
import { Tool } from '../../model/tool';
import { GroupedGrid } from '../../utils/grouped_grid';
import { ModalDialog } from '../../utils/ui-elements/modal-dialog/modal-dialog';
import { Message } from '../../model/message';
import { ApplicationRow } from './application.row';
import { VersionRow } from '../version/version.row';
import { NodeJSIndexComponent } from '../index.component';
import { AppService } from '../../../selector/app.service';
import { Mixin } from '../../utils/mixins/Mixin';
import { Filterable } from '../../utils/mixins/Filterable';
import { TranslateClass } from '../../utils/pipes/translate';
import { WindowRef } from '../../services/window.service';
import { LoaderSelector } from '../../utils/loader';

@Component({
    selector: 'node-js-application',
    template: require('./application.component.html'),
    styles: [require('./application.component.less')],
    providers: [SelectorRepo]
})
@Mixin([Filterable])
export class NodeJSApplicationComponent extends BaseComponent implements AfterViewInit {

    public rows: VersionRow[] | ApplicationRow[] = [];
    public columns: Column[] = [];
    public caption: Caption = new Caption(
        'Domains for Node.js ...',
        new Icon(
            'Back',
            () => this.router.navigate(
                ['/options', {outlets: {nodejs: ['versions']}}]
            )
        )
    );
    public tools: Tool[] = [
        new Tool(
            'refresh', new Message('Refresh'), 'refresh',
            () => this.loadAppsTable(true)
        ),
        // @TODO: return this tool when utils will be redy to do it
        // new Tool(
        //     'move', new Message('Move'), 'move',
        //     () => this.moveChosen(), null, true
        // ),
    ];
    public selectedVersion: string;
    public data: NodeJSInformation = null;

    @ViewChild(GroupedGrid) grid: GroupedGrid;
    @ViewChild('changeAppVersionWindow') changeAppVersionWindow: ModalDialog;

    public headerForChangeAppVersionWindow: Message;

    private readonly currentVersionName = new TranslateClass().transform(
        'Current version'
    );
    private _newVersion: string = this.currentVersionName;

    constructor(
        private selectorRepo: SelectorRepo,
        private parent: NodeJSIndexComponent,
        private router: Router,
        private route: ActivatedRoute,
        private winRef: WindowRef,
        private appService: AppService,
        private changeDetector: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit() {
        this.route.params.subscribe((params: {id: string}) => {
            this.selectedVersion = params.id;
        });
    }

    async ngAfterViewInit() {
        this.loaderSelector = new LoaderSelector('node-js grid', true);
        this.loadAppsTable();
    }

    public get defaultVersion(): string {
        return this.data.default_version;
    }

    public set newVersion(val: string) {
        this._newVersion = val;
        this.changeAppVersionWindow.disabledAgreeButton = (val === this.currentVersionName);
    }

    public get newVersion(): string {
        return this._newVersion;
    }

    public async loadAppsTable(
        refresh: boolean = false, resetRowCount: boolean = true,
        silent: boolean = false
    ) {
        try {
            if (!silent) this.loading = true;
            if (refresh) {
                await this.appService.update();
            }
            this.data = await this.appService.data();
        } finally {
            if (!silent) this.loading = false;
        }
        this.rows = this.prepareRowsForApplicationsTable(this.selectedVersion);
        this.columns = this.getColumnsForApplicationsTable();
        this.caption.title =  new TranslateClass().transform([
            'Domains for Node.js %(version)s', {version: this.selectedVersion}
        ]);
        let selectedMajorVersion = this.data.versions[this.selectedVersion].majorNumber;
        this.caption.label = (selectedMajorVersion === this.defaultVersion) ? 'default' : '';
        this.grid.loadMoreCount = 10;
        if (resetRowCount) this.grid.loadedRowCount = 3;
        this.grid.enableGroupOperation = false; // @TODO: return this tool when utils will be reаdy to do it
        this.grid.noFoundText = 'INFO.noApplicationsFound';
        this.parent.selectorEnabled = this.data.selector_enabled;
        this.changeDetector.detectChanges();
    };

    public async silentReloadAppsTable() {
        await this.loadAppsTable(true, false, true);
    }

    public getColumnsForApplicationsTable(): Column[] {
        return [
            new Column({name: 'appUri', descr: 'App URI', convertToFormat: 'string'})
                .setAction(async (event, row) => {
                    this.winRef.openExternalHttpTab(row.appUri);
                }),
            new Column({name: 'rootDir', descr: 'App Root Directory', convertToFormat: 'string'}),
            new Column({
                name: 'actions', descr: 'Actions', convertToFormat: 'string'
            }).sortableDisable()
        ];
    }

    public prepareRowsForApplicationsTable(version: string) {
        let users = this.data.versions[version].users;
        return Object.keys(users).reduce((rows, userName) => {
            let user = users[userName];
            let apps = user.applications;
            return rows.concat(
                Object.keys(apps).map((appName, index) => {
                    let app = apps[appName];
                    let row = new ApplicationRow();
                    row.rowId = index;
                    row.appUri = app.domain + '/' + app.app_uri;
                    row.rootDir = user.homedir + '/' + appName;
                    row.user = userName;
                    row.appName = appName;
                    row.version = version;
                    row.hasChangeButton = true;
                    row.checkedUpdated.subscribe(() => {
                        this.controlAbilityOfTools();
                    });
                    return row;
                })
            );
        }, []);
    }

    public actionClicked(event) {
        if (event.hasOwnProperty('action')
            && typeof this[event.action] === 'function'
        ) {
            this[event.action](event);
        }
    }

    /**
     * Open window for applications to another version
     *
     * @param event
     * @returns {Promise<void>}
     */
    public async moveAction(
        event: {event: Event, action: string, row: ApplicationRow}
    ) {
        if (event.row) {
            try {
                this.headerForChangeAppVersionWindow = new Message(
                    'Change Node.js version for application on %(uri)s',
                    {uri: event.row.appUri}
                );
                await this.changeAppVersionWindow.open();
                await this.changeAppVersion(event.row);
            } catch (e) {
                // Nothing to do
            } finally {
                this.newVersion = this.currentVersionName;
            }
        } else {
            console.log('===>', 'Wrong row format');
        }
    }

    public async changeAppVersion(row: ApplicationRow) {
        let appName = row.appName;
        let user = row.user;
        let version = this.data.versions[this.newVersion].majorNumber;
        try {
            row.loadingStage = true;
            await this.selectorRepo.changeAppVersion(appName, user, version);
            await this.silentReloadAppsTable();
        } catch (e) {
            console.log('===>', e);
        } finally {
            row.loadingStage = false;
            this.newVersion = this.currentVersionName;
        }
    }

    public get availableNewVersion() {
        return [this.currentVersionName].concat(this.data.getInstalledVersion().filter((version) => {
            return (version !== this.selectedVersion);
        }));
    }

    public async moveAll() {
        try {
            this.headerForChangeAppVersionWindow = new Message(
                'Change Node.js version for all applications'
            );
            await this.changeAppVersionWindow.open();
            this.loaderSelector = new LoaderSelector('node-js grid');
            this.loading = true;
            await this.selectorRepo.moveNodeJsVersion(
                this.selectedVersion, this.newVersion
            );
            delete this.data.versions[this.selectedVersion];
            await this.loadAppsTable(false, false);
        } catch (e) {
            // Nothing to do
            console.log('===>', e);
        } finally {
            this.newVersion = this.currentVersionName;
            this.loading = false;
        }
    }

    public multiChangeAppVersion() {
        return new Promise((resolve, reject) => {
            let requests = this.grid.originRows.map(async (row) => {
                return new Promise(async (resolve2) => {
                    if (row.checked) {
                        row.loadingStage = true;
                        await this.selectorRepo.changeAppVersion(
                            row['appName'], row['user'], row['version']
                        );
                        delete this.data
                            .versions[this.selectedVersion]
                            .users[row['user']]
                            .applications[row['appName']]; // TODO: remove this line where mock will be removed
                        row.loadingStage = false;
                    }
                    resolve2();
                });
            });
            Promise.all(requests).then(() => resolve());
        });
    }

    public async moveChosen() {
        if (this.grid.originRows.length === this.grid.numberOfChecked) {
            await this.moveAll();
            return;
        }
        try {
            this.headerForChangeAppVersionWindow = new Message(
                'Change Node.js version for chosen applications'
            );
            await this.changeAppVersionWindow.open();
            await this.multiChangeAppVersion();
            await this.loadAppsTable(false, false); // TODO: remove this line where mock will be removed
            // await this.silentReloadAppsTable(); // TODO: uncomment this line where mock will be removed
        } catch (e) {
            // Nothing to do
            console.log('===>', e);
        } finally {
            this.newVersion = this.currentVersionName;
        }
    }

    public controlAbilityOfTools() {
        this.tools.forEach((tool) => {
            if (tool.name === 'move') {
                tool.disabled = (this.grid.numberOfChecked === 0);
            }
        });
    }
}



// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./node_modules/angular-router-loader/src?loader=system&genDir=compiled&aot=false!./src/app/nodejs/application/application.component.ts